import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { CreateContentComponentTagBodyParams } from '~/services/ContentService';
import { CreateTagInput } from '~/services/TagsService';

import isProd from '~/utils/isProd';

import type { AssignmentFormData } from './TagAssignments/TagAssignments';
import { DEFAULT_TAG_CONTENT_FORM_DATA, type TagContentFormData } from './TagContent/TagContentForm';
import TagForm, { type FormTag } from './TagForm';
import { useCreateContentComponentTags, useCreateTag } from './hooks';

export default function TagCreate({
  onBack,
  onEditTag,
}: {
  onBack: () => void;
  onEditTag: (tagId: string, tab: string) => void;
}) {
  const [tab, setTab] = useState('assignments');
  const [assignments, setAssignments] = useState<Array<AssignmentFormData>>([]);
  const [contentError, setContentError] = useState<string | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();

  const createTag = useCreateTag();
  const createContentTags = useCreateContentComponentTags();

  const { control, handleSubmit } = useForm<FormTag>({
    defaultValues: {
      name: '',
      schedules: [],
    },
  });

  const {
    control: contentControl,
    reset: resetContent,
    getValues: getContentValues,
  } = useForm<TagContentFormData>({
    defaultValues: DEFAULT_TAG_CONTENT_FORM_DATA,
    mode: 'onChange',
  });

  const handleContentReset = (data: TagContentFormData) => {
    resetContent(data, { keepDirty: false });
  };

  const onSubmit = async (data: FormTag) => {
    if (assignments.length === 0) {
      enqueueSnackbar('Please add at least one assignment before creating the tag', { variant: 'error' });
      return;
    }

    if (data.schedules.length === 0) {
      enqueueSnackbar('Please add at least one schedule before creating the tag', { variant: 'error' });
      return;
    }

    const createTagData: CreateTagInput = {
      name: data.name,
      assignments: assignments.map((assignment) => ({
        entityType: assignment.entityType,
        entityId: assignment.entityId,
      })),
      schedules: data.schedules.map((schedule) => ({
        brands: schedule.brands,
        regions: schedule.regions,
        activeStartDate: schedule.activeStartDate,
        activeEndDate: schedule.activeEndDate,
        travelStartDate: schedule.travelStartDate || undefined,
        travelEndDate: schedule.travelEndDate || undefined,
      })),
    };

    const contentTagsParams: Array<CreateContentComponentTagBodyParams> = [];

    if (isProd()) {
      const contentData = getContentValues();
      const hasContentWithoutTitle = Object.values(contentData).some((content) => !content.title);
      if (hasContentWithoutTitle) {
        setContentError('Please fill all content types with a title');
        setTab('content');
        return;
      }
      setContentError(undefined);

      const contentTypes = ['offer-banner', 'search-banner', 'offer-pill', 'inflow-pill', 'search-pill'];

      for (const contentType of contentTypes) {
        const content = contentType.includes('pill')
          ? contentData.pill
          : contentType === 'offer-banner'
          ? contentData.offerBanner
          : contentData.searchBanner;

        if (!content?.title) {
          continue;
        }

        contentTagsParams.push({
          slug: `${contentType}-${data.name}`,
          brand: 'luxuryescapes',
          icon: content.icon,
          title: content.title,
          subtitle: content.subtitle || undefined,
          linkText: content.linkText || undefined,
        });
      }
    }

    const createdTag = await createTag.mutateAsync(createTagData);

    if (contentTagsParams.length > 0) {
      try {
        await createContentTags.mutateAsync(contentTagsParams);
      } catch {
        enqueueSnackbar('Tag created, but content tags failed. Redirecting to edit page...', { variant: 'warning' });
        onEditTag(createdTag.id, 'content');
        return;
      }
    }

    onBack();
  };

  const handleSave = handleSubmit(onSubmit, (errors) => {
    enqueueSnackbar(errors['schedules']?.message || 'Unknown error', { variant: 'error' });
  });

  return (
    <TagForm
      mode="create"
      tab={tab}
      control={control}
      isSaving={createTag.isPending || createContentTags.isPending}
      assignments={assignments}
      onAssignmentsChange={setAssignments}
      onBack={onBack}
      onSave={handleSave}
      onTabChange={setTab}
      contentControl={contentControl}
      onContentReset={handleContentReset}
      contentError={contentError}
    />
  );
}
